import {MOON_500} from '@wandb/weave/common/css/globals.styles';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {RemovableTag, RemoveAction} from '@wandb/weave/components/Tag';
import React from 'react';
import {
  components,
  DropdownIndicatorProps,
  GroupBase,
  MenuProps,
  MultiValue,
  MultiValueProps,
  OptionProps,
} from 'react-select';

// Team option type for react-select
export interface TeamOption {
  label: string;
  value: string;
  userCount: number;
}

// Custom DropdownIndicator that shows even for multi-select
// This is because the default DropdownIndicator only shows for single-select
const TeamDropdownIndicator = (
  indicatorProps: DropdownIndicatorProps<
    TeamOption,
    true,
    GroupBase<TeamOption>
  >
) => {
  const iconName = indicatorProps.selectProps.menuIsOpen
    ? 'chevron-up'
    : 'chevron-down';
  return (
    <components.DropdownIndicator {...indicatorProps}>
      <Icon name={iconName} width={16} height={16} color={MOON_500} />
    </components.DropdownIndicator>
  );
};

// Custom components for react-select team dropdown
const TeamOptionComponent = (props: OptionProps<TeamOption, true>) => {
  return (
    <components.Option
      {...props}
      className="flex items-center justify-between py-8 px-12 hover:bg-moon-100">
      <div className="flex items-center gap-8">
        <Icon name="users-team" width={16} height={16} />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-moon-800">
            {props.data.label}
          </span>
          <span className="text-xs text-moon-500">
            {props.data.userCount} users
          </span>
        </div>
      </div>
    </components.Option>
  );
};

const TeamMultiValue = (
  props: MultiValueProps<TeamOption, true, GroupBase<TeamOption>>
) => {
  return (
    <div
      onMouseDown={e => {
        e.preventDefault();
        e.stopPropagation();
      }}>
      <RemovableTag
        color="moon"
        label={props.data.label}
        removeAction={
          <RemoveAction
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              props.removeProps.onClick?.(e as any);
            }}
          />
        }
        Wrapper={null}
      />
    </div>
  );
};

const TeamDropdownHeader = ({
  availableTeamCount,
}: {
  availableTeamCount: number;
}) => (
  <div className="px-12 py-6 flex items-center justify-between">
    <span className="text-md font-semibold text-moon-800">Teams</span>
    <div className="flex items-center justify-center h-20 w-20 rounded-full bg-moon-150 text-xs font-medium text-moon-600">
      {availableTeamCount}
    </div>
  </div>
);

const CustomMenu = (props: MenuProps<TeamOption, true>) => {
  const totalTeamCount = props.options.length;
  const selectedTeamCount = Array.isArray(props.selectProps.value)
    ? props.selectProps.value.length
    : 0;
  const availableTeamCount = totalTeamCount - selectedTeamCount;

  return (
    <components.Menu {...props}>
      <TeamDropdownHeader availableTeamCount={availableTeamCount} />
      {props.children}
    </components.Menu>
  );
};

export const TeamSelect = ({
  teamOptions,
  teams,
  setTeams,
}: {
  teamOptions: TeamOption[];
  teams: string[];
  setTeams: (teams: string[]) => void;
}) => {
  return (
    <Select
      isMulti
      options={teamOptions}
      value={teamOptions.filter(option => teams.includes(option.value))}
      onChange={(selectedOptions: MultiValue<TeamOption>) => {
        const selectedValues = selectedOptions
          ? selectedOptions.map(option => option.value)
          : [];
        setTeams(selectedValues);
      }}
      size="variable"
      placeholder="Select which teams you'd like them to join."
      components={{
        Option: TeamOptionComponent,
        Menu: CustomMenu,
        DropdownIndicator: TeamDropdownIndicator,
        MultiValue: TeamMultiValue,
      }}
      closeMenuOnSelect={false}
      hideSelectedOptions={true}
      menuPlacement="auto"
    />
  );
};
